import * as React from 'react';
import {
    TextInput,
    DateTimeInput,
    required,
    SimpleForm,
    SelectInput,
    FormDataConsumer,
    BooleanInput,
    ArrayInput,
    SelectArrayInput,
    useNotify,
    useRedirect,
} from 'react-admin';
import { useParams } from 'react-router';
import { LoadingSection } from '../../common/LoadingSection';
import { STATUS, TEST_CATEGORIES, TEST_TYPES } from '../../common/constants';
import HTMLEditorInput, { validHtml } from '../../common/inputs/HTMLEditorInput';
import SlugInput from '../../common/inputs/SlugInput';
import PdfAutocompleteInput from '../../common/inputs/PdfAutoCompleteInput';
import PdfUrlPrefilled from '../../common/inputs/PdfUrlPrefilled';
import useTestLanguages from '../../hooks/useTestLanguages';
import { Box, Tab, Tabs, Typography } from '@mui/material';
import FileUpload from '../../common/inputs/FileUpload';
import { restDataProvider } from '../../dataProviders/restDataProvider/restDataProvider';
import { TestSeriesAutocomplete } from '../../common/inputs/autoCompleteInputs/TestSeriesAutoCompleteInput';
import VideoAutocompleteInput from '../../common/inputs/VideoAutocompleteInput';

export function TabPanel(props: any) {
    const { children, value, index, ...other } = props;
    return (
        <div role="tabpanel" hidden={value !== index} id={`tabpanel-${index}`} {...other}>
            {value === index && (
                <Box sx={{ p: 3 }}>
                    <Typography>{children}</Typography>
                </Box>
            )}
        </div>
    );
}

export const LanguageFields = ({ language }: { language: string }) => {
    return (
        <>
            <HTMLEditorInput
                source={`${language} generalInstructions`}
                fullWidth
                validate={[required(), validHtml]}
            />
            <HTMLEditorInput
                source={`${language} testInstructions`}
                fullWidth
                validate={[required(), validHtml]}
            />
        </>
    );
};

export function transformTestData(data: any, languages: string[]) {
    const formData = { ...data };
    const testLanguages: any[] = [];

    languages.forEach(langCode => {
        const generalKey = `${langCode} generalInstructions`;
        const testKey = `${langCode} testInstructions`;

        const generalInstructions = formData[generalKey];
        const testInstructions = formData[testKey];

        delete formData[generalKey];
        delete formData[testKey];

        if (generalInstructions || testInstructions) {
            testLanguages.push({
                language: langCode,
                generalInstructions: generalInstructions || '',
                testInstructions: testInstructions || '',
            });

            if (!formData.generalInstructions) {
                formData.generalInstructions = generalInstructions;
            }
            if (!formData.testInstructions) {
                formData.testInstructions = testInstructions;
            }
        }
    });

    return {
        ...formData,
        category: formData.pattern || 'OBJECTIVE',
        displayOrder: Number(formData.displayOrder),
        maxDurationInMinutes: Number(formData.maxDurationInMinutes),
        testLanguages: testLanguages,
    };
}

const TestCreate = () => {
    const [selectedPdfUrl, setSelectedPdfUrl] = React.useState('');
    const { testSeriesId } = useParams();

    if (!testSeriesId) return <LoadingSection />;

    const { languages, loading, error } = useTestLanguages();

    const [languagesList, setLanguagesList] = React.useState<any[]>([]);
    const [selectedLanguages, setSelectedLanguages] = React.useState<string[]>([]);
    const [value, setValue] = React.useState(0);

    const notify = useNotify();
    const redirect = useRedirect();

    const handleChange = (_: any, newValue: number) => setValue(newValue);

    const getUiForTab = (tabIndex: number) => (
        <LanguageFields language={selectedLanguages[tabIndex]} />
    );

    React.useEffect(() => {
        if (languages && typeof languages === 'object') {
            const formatted = Object.entries(languages).map(([fullName, code]) => ({
                id: code,
                name: fullName,
            }));
            setLanguagesList(formatted);
        }
    }, [languages]);

    const modifiedPostSave = async (data: any) => {
        try {
            const createData = transformTestData(data, selectedLanguages);

            await restDataProvider.create('tests', {
                data: createData,
            });

            notify('Test created successfully', { type: 'success' });
            redirect(`/test-series/${testSeriesId}/tests`);
        } catch (error: any) {
            notify(`Error: ${error.message}`, { type: 'warning' });
        }
    };

    if (loading) return <LoadingSection />;

    if (error) {
        return (
            <div className="flex justify-center items-center h-[90vh]">
                <div className="bg-red-50 text-red-500 px-20 p-4">
                    Something went wrong! {error}
                </div>
            </div>
        );
    }

    return (
        <Box sx={{ width: '100%' }} className="border m-2 p-3 rounded-lg ">
            <SimpleForm onSubmit={modifiedPostSave} defaultValues={{ testSeriesId: testSeriesId }}>
                <FileUpload
                    type="VIDEO_THUMBNAIL"
                    label="Select Image"
                    source="image"
                    accept="image/*"
                    multiple={false}
                />
                <TestSeriesAutocomplete readOnly />

                <TextInput source="name" fullWidth validate={[required()]} />
                <SlugInput source="slug" fullWidth />

                <TextInput source="displayOrder" type="number" fullWidth validate={[required()]} />

                <SelectInput
                    source="type"
                    label="Type"
                    fullWidth
                    validate={[required()]}
                    choices={TEST_TYPES}
                />

                <TextInput source="tag" fullWidth />
                <DateTimeInput source="resultGenerationTime" fullWidth />

                <BooleanInput source="reattemptAllowed" defaultValue={true} />
                <BooleanInput source="isDemo" defaultValue={false} />

                <SelectInput
                    source="pattern"
                    label="Category"
                    fullWidth
                    defaultValue="OBJECTIVE"
                    validate={[required()]}
                    choices={TEST_CATEGORIES}
                />

                <SelectInput
                    source="status"
                    label="Status"
                    fullWidth
                    validate={[required()]}
                    choices={STATUS}
                    defaultValue="ACTIVE"
                />

                <DateTimeInput source="startTime" fullWidth validate={[required()]} />
                <DateTimeInput source="endTime" fullWidth validate={[required()]} />
                <DateTimeInput source="validTill" label="Test Valid Till" fullWidth />

                <TextInput
                    type="number"
                    source="maxDurationInMinutes"
                    fullWidth
                    validate={[required()]}
                />

                <FileUpload
                    type="PDF_NOTES"
                    source="solutionFiles"
                    label="Select Solution Files"
                    accept="application/pdf"
                    multiple={true}
                />
                <VideoAutocompleteInput
                    source="solutionVideoId"
                    formData={{}}
                />

                <FormDataConsumer>
                    {({ formData }) => (
                        <>
                            <PdfAutocompleteInput
                                fullWidth
                                formData={formData}
                                source="pdfId"
                                selectedPdfUrl={selectedPdfUrl}
                                setSelectedPdfUrl={setSelectedPdfUrl}
                            />

                            <PdfUrlPrefilled
                                fullWidth
                                formData={formData}
                                source="questionFileId"
                                selectedPdfUrl={selectedPdfUrl}
                            />
                        </>
                    )}
                </FormDataConsumer>

                {/* ----------------- LANGUAGE SELECTION ----------------- */}
                <ArrayInput source="languages" defaultValue={selectedLanguages}>
                    <SelectArrayInput
                        label="Test Languages"
                        fullWidth
                        choices={languagesList}
                        validate={[required()]}
                        onChange={e => setSelectedLanguages(e.target.value)}
                    />
                </ArrayInput>

                {/* ----------------- TABS ----------------- */}
                <Tabs value={value} onChange={handleChange}>
                    {selectedLanguages.map((code, index) => {
                        const label = languagesList.find(l => l.id === code)?.name || code;
                        return <Tab key={index} label={label} />;
                    })}
                </Tabs>

                {selectedLanguages.map((lang, index) => (
                    <TabPanel key={lang} value={value} index={index}>
                        {getUiForTab(index)}
                    </TabPanel>
                ))}
            </SimpleForm>
        </Box>
    );
};

export default TestCreate;
